import { IntersectionObserverInitFactory } from '.';
import { FEATURE_APP_NAME_UNKNOWN } from './models';
/**
 * A stateful container for managing impressions.
 */
export class ImpressionManager {
    logger;
    track;
    impressionsByElement = new Map();
    constructor(logger, track) {
        this.logger = logger;
        this.track = track;
    }
    add(featureAppInfo, element, impressions) {
        this.logger.debug(`Impression tracking registered on element '${element?.tagName}' with delay`, impressions.map((i) => i.delay));
        const impressionList = this.createImpressionListForElement(featureAppInfo, impressions);
        if (!this.impressionsByElement.has(element)) {
            this.impressionsByElement.set(element, impressionList);
            impressionList.impressions.forEach((impressionWrapper) => {
                Object.values(impressionWrapper.observers).forEach((observerWrapper) => {
                    observerWrapper.observer.observe(element);
                });
            });
        }
        else {
            throw new Error(`The element of the feature app ${featureAppInfo.name ?? FEATURE_APP_NAME_UNKNOWN} (id: ${featureAppInfo.id}) is already registered: ${element.tagName}`);
        }
    }
    remove(element) {
        this.removeImpressionObservers(element);
        this.impressionsByElement.delete(element);
    }
    createImpressionListForElement(featureAppInfo, impressions) {
        const impressionList = {
            featureAppInfo,
            impressions: [],
        };
        // handle each impression configuration on its own
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < impressions.length; i++) {
            const impressionConfig = impressions[i];
            /* This observer should trigger at any intersection in the upper 70%.
             * With the -30% in the rootMargin we reduce the intersection space to the bottom
             * and only the upper 70% of the space will trigger the observer.
             */
            const init70 = IntersectionObserverInitFactory.create70();
            const init100 = IntersectionObserverInitFactory.create100();
            const observer70 = new IntersectionObserver(this.visibilityCallback.bind(this, i, 'Observer70'), init70);
            const observer100 = new IntersectionObserver(this.visibilityCallback.bind(this, i, 'Observer100'), init100);
            // 1 Element can have multiple impressions, each impression has exactly 2 observers
            impressionList.impressions.push({
                config: impressionConfig,
                fired: false,
                observers: {
                    Observer70: {
                        observer: observer70,
                        isVisible: false,
                    },
                    Observer100: {
                        observer: observer100,
                        isVisible: false,
                    },
                },
            });
        }
        return impressionList;
    }
    visibilityCallback(impressionId, observerType, entries) {
        entries.forEach((intersectionEntry) => {
            const element = intersectionEntry.target;
            const impressionList = this.impressionsByElement.get(element);
            if (!impressionList) {
                this.removeImpressionObservers(element);
                return;
            }
            const impressionWrapper = impressionList.impressions[impressionId];
            if (!impressionWrapper || impressionWrapper.fired) {
                this.unobserveImpression(element, impressionWrapper);
                return;
            }
            const currentObserver = impressionWrapper.observers[observerType];
            let isVisible = intersectionEntry.intersectionRatio > 0;
            if (observerType === 'Observer100') {
                isVisible = intersectionEntry.intersectionRatio === 1;
            }
            currentObserver.isVisible = isVisible;
            if (isVisible && !currentObserver.timeoutId) {
                currentObserver.timeoutId = setTimeout(this.triggerTracking.bind(this, element, impressionId, observerType), impressionWrapper.config.delay);
            }
            else if (!isVisible && currentObserver.timeoutId) {
                clearTimeout(currentObserver.timeoutId);
                currentObserver.timeoutId = undefined;
            }
        });
    }
    triggerTracking(element, impressionId, observerType) {
        const impressionList = this.impressionsByElement.get(element);
        const impressionWrapper = impressionList?.impressions[impressionId];
        const observerWrapper = impressionWrapper?.observers[observerType];
        if (observerWrapper && !impressionWrapper.fired && observerWrapper.isVisible) {
            impressionWrapper.fired = true;
            this.unobserveImpression(element, impressionWrapper);
            const event = impressionWrapper.config.createImpressionEvent();
            this.logger.debug(`Executing impression event after delay of ${impressionWrapper.config.delay} ms`, event);
            this.track(impressionList.featureAppInfo, event);
        }
        else if (!impressionList) {
            this.logger.warn(`Could not find tracking info for the element: '${element.tagName}'`);
        }
    }
    removeImpressionObservers(element) {
        const impressionList = this.impressionsByElement.get(element);
        if (impressionList) {
            impressionList.impressions.forEach((impressionWrapper) => this.unobserveImpression(element, impressionWrapper));
        }
    }
    // eslint-disable-next-line class-methods-use-this
    unobserveImpression(element, impressionWrapper) {
        Object.values(impressionWrapper.observers).forEach((observerWrapper) => {
            observerWrapper.observer.unobserve(element);
            if (observerWrapper.timeoutId) {
                clearTimeout(observerWrapper.timeoutId);
                // eslint-disable-next-line no-param-reassign
                observerWrapper.timeoutId = undefined;
            }
        });
    }
}
